import React, { useContext, useEffect } from 'react';
import { GetServerSidePropsContext } from 'next';
import { useRouter } from 'next/router';

import { AdPageResponseType, BrowserProps, TemplateType } from '../../../types';

import { fetchTemplates, getAdPage } from '../../../lib/API';
import { CMSDimensionType } from '../../../constants/common';
import useLazyload from 'src/hooks/useLazyload';
import { AnalyticsUtils } from '@invideoio/web-shared/utils/analytics/AnalyticsUtils';
import { registerAndSendPageViewEvent } from '@invideoio/web-shared/utils/analytics/Register';
import { Referrer } from '@invideoio/web-shared/utils/utils.constant';
import { PageType } from '@invideoio/web-shared/types/Analytics';
import { AmplitudeEvents, sendAmplitudeAnalyticEvent } from '@utils/analytics';
import {
  attachTemplates,
  extractTemplateDimensions,
  extractTemplateIds,
  filterBlogResponseForCards,
} from '@utils/common.utils';
import MakeSEO from '@components/common/SEO/MakeSEO';
import { DispatchContext } from '@store/store';
import {
  baseAppTypeLoaded,
  setEditorParams,
  setRedirectionLink,
} from '@store/actions';
import useTranslation from 'next-translate/useTranslation';
import AdPageContainer from '@layouts/AdPageContainer';
import { setCacheControlHeaders } from '@utils/server.utils';

interface MakeProps extends BrowserProps {
  makePageState: AdPageResponseType;
  isMakeV3: boolean;
}

const Make = (props: MakeProps) => {
  const { makePageState, isMakeV3 } = props;
  useLazyload();

  const router = useRouter();
  const dispatch = useContext(DispatchContext);

  const { lang } = useTranslation('make');

  const url_path =
    router.locale === 'en'
      ? router.asPath
      : `/${router.locale}${router.asPath}`;

  useEffect(() => {
    const AppName = process.env.NEXT_PUBLIC_APP_NAME;

    if (AppName === 'kizoa') {
      AnalyticsUtils.setReferrer(Referrer.KIZOA);
    } else {
      AnalyticsUtils.setReferrer(Referrer.WEBSITE);
    }

    registerAndSendPageViewEvent(PageType.MAKE, {
      location: window.location.href,
    });

    sendAmplitudeAnalyticEvent(AmplitudeEvents.appLoad, {
      'Make page Revamp Treatment': 1,
    });

    if (makePageState.heroSection?.CTAUrl) {
      let redirectURL = makePageState.heroSection?.CTAUrl;
      if (!redirectURL.match(/^https?:\/\/(\S+\.)?invideo\.io/)) {
        const host = process.env.NEXT_PUBLIC_BASE_HOST || 'https://invideo.io';
        redirectURL = `${host}${redirectURL}`;
      }
      dispatch(setRedirectionLink(redirectURL));
    }

    if (makePageState.ExtraData) {
      const { searchTerm, tabType, categoryTray } = makePageState.ExtraData;

      dispatch(setEditorParams({ searchTerm, tabType, categoryTray }));
    }

    if (makePageState.BaseAppType) {
      dispatch(baseAppTypeLoaded(makePageState.BaseAppType));
    }
  }, []);

  return (
    <React.Fragment>
      <MakeSEO
        seo={makePageState.SEO}
        url={url_path}
        FAQItems={makePageState.FaqItems}
        HowToSchema={makePageState.HowToSchema}
        locale={lang}
      />

      {makePageState && <AdPageContainer makePageState={makePageState} />}
    </React.Fragment>
  );
};

export async function getServerSideProps(ctx: GetServerSidePropsContext) {
  let makePageState = null;
  let isMakeV3 = false;
  try {
    const lang = getLanguage(ctx.locale);
    const slug = getSlug(ctx.query.id, ctx.locale);
    const appName = process.env.NEXT_PUBLIC_APP_NAME;

    if (appName === 'invideo') {
      try {
        makePageState = await getAdPage(slug, lang);

        if (makePageState) {
          if (Array.isArray(makePageState.invideo_blogs)) {
            makePageState.invideo_blogs = filterBlogResponseForCards(
              makePageState.invideo_blogs,
            );
          }
          isMakeV3 = true;
        }
      } catch (error) {
        console.log(`Unable to fetch Make V3 post`);
      }
    }

    if (appName === 'invideo' && !makePageState) {
      makePageState = await getAdPage(slug, lang);
    }

    if (
      makePageState &&
      makePageState.templateSlide_1 &&
      makePageState.templateSlide_2 &&
      makePageState.templateSlide_3 &&
      makePageState.templateSlide_4
    ) {
      const ids = extractTemplateIds(
        makePageState.templateSlide_1,
        makePageState.templateSlide_2,
        makePageState.templateSlide_3,
        makePageState.templateSlide_4,
      );
      const dimensions: CMSDimensionType[] = extractTemplateDimensions(
        makePageState.templateSlide_1,
        makePageState.templateSlide_2,
        makePageState.templateSlide_3,
        makePageState.templateSlide_4,
      );
      const templates: TemplateType[] = await fetchTemplates(ids, dimensions);

      if (ids.length !== templates.length) {
        throw `Could not fetch few images from ${ids} got ${templates}`;
      }
      makePageState = {
        ...makePageState,
        ...attachTemplates({
          templateSlide1: makePageState.templateSlide_1,
          templateSlide2: makePageState.templateSlide_2,
          templateSlide3: makePageState.templateSlide_3,
          templateSlide4: makePageState.templateSlide_4,
          images: templates,
        }),
      };
    }
    //mini video wall slide
    if (makePageState && makePageState.videoWallSlide) {
      const ids = extractTemplateIds(makePageState.videoWallSlide);
      const dimensions: CMSDimensionType[] = extractTemplateDimensions(
        makePageState.videoWallSlide,
      );
      const templates: TemplateType[] = await fetchTemplates(ids, dimensions);
      if (ids.length !== templates.length) {
        throw `Could not fetch few images from ${ids} got ${templates}`;
      }
      const attachedTemplates = attachTemplates({
        templateSlide1: makePageState.videoWallSlide,
        images: templates,
      });

      makePageState = {
        ...makePageState,
        videoWallSlide: attachedTemplates.templateSlide_1,
      };
    }
    //ai actor templates

    if (
      makePageState &&
      makePageState.aiActorSlide_1 &&
      makePageState.aiActorSlide_2 &&
      makePageState.aiActorSlide_3 &&
      makePageState.aiActorSlide_4
    ) {
      const actorIds = extractTemplateIds(
        makePageState.aiActorSlide_1,
        makePageState.aiActorSlide_2,
        makePageState.aiActorSlide_3,
        makePageState.aiActorSlide_4,
      );
      const actorDimensions: CMSDimensionType[] = extractTemplateDimensions(
        makePageState.aiActorSlide_1,
        makePageState.aiActorSlide_2,
        makePageState.aiActorSlide_3,
        makePageState.aiActorSlide_4,
      );
      const actorTemplates: TemplateType[] = await fetchTemplates(
        actorIds,
        actorDimensions,
      );
      if (actorIds.length !== actorTemplates.length) {
        throw `Could not fetch few images from ${actorIds} got ${actorTemplates}`;
      }
      const attachedActorTemplates = attachTemplates({
        templateSlide1: makePageState.aiActorSlide_1,
        templateSlide2: makePageState.aiActorSlide_2,
        templateSlide3: makePageState.aiActorSlide_3,
        templateSlide4: makePageState.aiActorSlide_4,
        images: actorTemplates,
      });

      makePageState = {
        ...makePageState,
        aiActorSlide_1: attachedActorTemplates.templateSlide_1,
        aiActorSlide_2: attachedActorTemplates.templateSlide_2,
        aiActorSlide_3: attachedActorTemplates.templateSlide_3,
        aiActorSlide_4: attachedActorTemplates.templateSlide_4,
      };
    }
  } catch (error) {
    console.log(error);
    return { notFound: true };
  }

  let maxage = 3600;

  if (process.env.NEXT_PUBLIC_ENV !== 'production') {
    maxage = 300; // 5mins
  }

  setCacheControlHeaders(ctx, maxage);

  return { props: { makePageState, isMakeV3 } };
}

function getLanguage(locale?: string) {
  if (locale === 'jp') {
    return 'ja';
  }

  if (locale === 'kr') {
    return 'ko';
  }

  return locale || 'en';
}

function getSlug(queryId: string | string[] | undefined, locale?: string) {
  if (!locale || locale === 'en') {
    return queryId as string;
  }
  return `${locale}-${queryId}`;
}

export default Make;
